import {
	GET_ORDER_DETAILS,
	GET_ORDER_DETAILS_START,
	LIST_MY_WORKFLOW_ORDERS,
	LIST_MY_WORKFLOW_ORDERS_START,
	REVIEW,
	REVIEW_START,
} from '@app/api/action/ApprovalWorkflow';
import { RootReducer } from '@app/app.reducers';
import { setName } from '@app/shared/utils/name-helper';
import {
	ApiAction,
	Failure,
	Initialized,
	Kinds,
	Pending,
	Success,
	type RemoteData,
} from '@granodigital/grano-remote-data';
import { createSelector } from 'reselect';

export interface State {
	orders: RemoteData<unknown, api.OrderDto[]>;
	reviewSubmission: RemoteData<unknown, api.SuccessDto>;
	orderDetails: RemoteData<unknown, api.OrderDto>;
	currentPage: number;
	pageSize: number;
	totalItems: number;
	totalPages: number;
}

export const initialState: State = {
	orders: new Initialized(),
	reviewSubmission: new Initialized(),
	orderDetails: new Initialized(),
	currentPage: 0,
	pageSize: 10,
	totalItems: 0,
	totalPages: 0,
};

export const RESET_STATUSES = 'mygrano/approval/RESET_STATUSES';
export const resetStatuses = () => ({ type: RESET_STATUSES }) as const;

export const SET_CURRENT_PAGE = 'mygrano/approval/SET_CURRENT_PAGE';
export const setCurrentPage = (page: number) => ({ type: SET_CURRENT_PAGE, payload: page }) as const;

export const SET_PAGE_SIZE = 'mygrano/approval/SET_PAGE_SIZE';
export const setPageSize = (pageSize: number) => ({ type: SET_PAGE_SIZE, payload: pageSize }) as const;

type ApprovalAction =
	| ReturnType<typeof resetStatuses>
	| ReturnType<typeof setCurrentPage>
	| ReturnType<typeof setPageSize>
	| ApiAction<typeof LIST_MY_WORKFLOW_ORDERS_START, never>
	| ApiAction<typeof LIST_MY_WORKFLOW_ORDERS, { data: api.OrderDto[]; totalCount: number }>
	| ApiAction<typeof REVIEW_START, never>
	| ApiAction<typeof REVIEW, api.SuccessDto>
	| ApiAction<typeof GET_ORDER_DETAILS_START, never>
	| ApiAction<typeof GET_ORDER_DETAILS, api.OrderDto>;

/** Reducer for approval workflow state. */
export function reducer(state = initialState, action: ApprovalAction): State {
	switch (action.type) {
		case LIST_MY_WORKFLOW_ORDERS_START:
			return { ...state, orders: new Pending() };

		case LIST_MY_WORKFLOW_ORDERS: {
			if (action.error === true) {
				return { ...state, orders: new Failure(action.payload) };
			}

			// Handle API response format: { data: OrderDto[], totalCount: number }
			const { data: ordersData = [], totalCount: totalItems = 0 } = action.payload;

			return {
				...state,
				orders: new Success(ordersData),
				totalItems,
				totalPages: Math.ceil(totalItems / state.pageSize),
			};
		}

		case REVIEW_START:
			return { ...state, reviewSubmission: new Pending() };

		case REVIEW: {
			if (action.error === true) {
				return { ...state, reviewSubmission: new Failure(action.payload) };
			}
			return { ...state, reviewSubmission: new Success(action.payload) };
		}

		case GET_ORDER_DETAILS_START:
			return {
				...state,
				orderDetails: new Pending(),
			};

		case GET_ORDER_DETAILS:
			if (action.error === true) {
				return {
					...state,
					orderDetails: new Failure(action.payload),
				};
			}
			return {
				...state,
				orderDetails: new Success(action.payload),
			};

		case SET_CURRENT_PAGE:
			return { ...state, currentPage: action.payload };

		case SET_PAGE_SIZE:
			return {
				...state,
				pageSize: action.payload,
				currentPage: 0, // Reset to first page when changing page size
				totalPages: Math.ceil(state.totalItems / action.payload),
			};

		case RESET_STATUSES:
			return {
				...state,
				orders: new Initialized(),
				reviewSubmission: new Initialized(),
				orderDetails: new Initialized(),
				currentPage: 0,
				totalItems: 0,
				totalPages: 0,
			};

		default:
			return state;
	}
}

// Selectors
export const getState = setName('getState', (state: RootReducer.State) => state.approvals);

export const getOrdersRemoteData = setName(
	'getOrdersRemoteData',
	createSelector(getState, (state) => state.orders),
);

export const getMyApprovalOrders = setName(
	'getMyApprovalOrders',
	createSelector(getOrdersRemoteData, (ordersRemoteData) => {
		if (ordersRemoteData.kind === Kinds.Success) {
			return ordersRemoteData.data;
		}
		return [];
	}),
);

export const isLoadingOrders = setName(
	'isLoadingOrders',
	createSelector(getOrdersRemoteData, (ordersRemoteData) => ordersRemoteData.kind === Kinds.Pending),
);

export const hasLoadingError = setName(
	'hasLoadingError',
	createSelector(getOrdersRemoteData, (ordersRemoteData) => ordersRemoteData.kind === Kinds.Failure),
);

export const getReviewSubmissionRemoteData = setName(
	'getReviewSubmissionRemoteData',
	createSelector(getState, (state) => state.reviewSubmission),
);

export const isSubmitSuccess = setName(
	'isSubmitSuccess',
	createSelector(
		getReviewSubmissionRemoteData,
		(reviewRemoteData) => reviewRemoteData.kind === Kinds.Success,
	),
);

export const hasSubmitError = setName(
	'hasSubmitError',
	createSelector(
		getReviewSubmissionRemoteData,
		(reviewRemoteData) => reviewRemoteData.kind === Kinds.Failure,
	),
);

// Order details selectors
export const getOrderDetailsRemoteData = setName(
	'getOrderDetailsRemoteData',
	createSelector(getState, (state) => state.orderDetails),
);

export const getOrderDetails = setName(
	'getOrderDetails',
	createSelector(getOrderDetailsRemoteData, (orderDetailsRemoteData) => {
		if (orderDetailsRemoteData.kind === Kinds.Success) {
			return orderDetailsRemoteData.data;
		}
		return undefined;
	}),
);

// Pagination selectors
export const getCurrentPage = setName(
	'getCurrentPage',
	createSelector(getState, (state) => state.currentPage),
);

export const getPageSize = setName(
	'getPageSize',
	createSelector(getState, (state) => state.pageSize),
);

export const getTotalItems = setName(
	'getTotalItems',
	createSelector(getState, (state) => state.totalItems),
);

export const getTotalPages = setName(
	'getTotalPages',
	createSelector(getState, (state) => state.totalPages),
);
